import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  Grid,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
  useMediaQuery,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Chip,
  Switch,
  IconButton,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import React, { useEffect, useState } from 'react';
import { PriceDialog } from './Price/PriceDialog';
import { useTheme } from '@emotion/react';
import { useToastContext } from './toast-context';
import { services } from '../pages/Sell/services';
import ConfirmFloorPriceDialog from './confirm-floor-price-dialog'; // ایمپورت دایلاگ جدید

const PreviewPrice = ({
  manualBasePrice,
  setShowPreview,
  setProductConfig,
  changedPreviewProductConfig,
  setBullionsConfig,
  configValues,
  trig,
  trigger,
  type,
  openDialog,
  bullionTypes,
  manualPriceLessThanThreshhold,
  manualPriceMoreThanThreshhold,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isPreviewSubmitting, setIsPreviewSubmitting] = React.useState();

  const [calculateFloorPriceForSell, setCalculateFloorPriceForSell] = useState(
    !!configValues?.calculateFloorPriceForSell
  );

  const [checkedRows, setCheckedRows] = useState(new Set());
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openFloorPriceConfirm, setOpenFloorPriceConfirm] = useState(false);

  const { showToast } = useToastContext();

  const commaRemovedManualBasePrice = !!String(
    configValues?.manualBasePrice
  )?.includes(',')
    ? Number(configValues.manualBasePrice.replace(/,/g, ''))
    : configValues.manualBasePrice;

  const firstCategory = bullionTypes.slice(0, 7);
  const secondCategory = bullionTypes.slice(7);

  const isAnyChecked = checkedRows.size > 0;
  const checkedBullions = bullionTypes.filter(b => checkedRows.has(b.key));

  const handleSave = async () => {
    try {
      setIsPreviewSubmitting(true);
      const config = {
        strategy: 'MANUAL',
        calculateFloorPriceForSell: !calculateFloorPriceForSell,
      };

      if (
        commaRemovedManualBasePrice !== null &&
        commaRemovedManualBasePrice !== undefined
      ) {
        config.manualBasePrice =
          manualBasePrice === '0' ? 0 : commaRemovedManualBasePrice;
      }

      await services.setProductConfig({
        isApplyingToAll: true,
        transactionType: type,
        ...config,
      });
      showToast('قیمت جدید با موفقیت ثبت شد.', 'success');

      await Promise.all(
        changedPreviewProductConfig.map(async product => {
          const { productType, ...rest } = product;
          await setProductConfig(rest, productType);
        })
      );

      setIsPreviewSubmitting(false);
      setShowPreview(false);
      setOpenConfirmModal(false);
      trig();
    } catch (error) {
      console.error('Error submitting configuration:', error);
      let toastMessage = error.response?.data?.message || 'خطایی رخ داد.';
      showToast(toastMessage, 'error');
      setIsPreviewSubmitting(false);
    }
  };

  const handleToggleSwitchChange = e => {
    const checked = e.target.checked;
    if (checked) {
      setOpenFloorPriceConfirm(true);
    } else {
      setCalculateFloorPriceForSell(false);
    }
  };

  const handleConfirmFloorPrice = () => {
    setCalculateFloorPriceForSell(true);
    setOpenFloorPriceConfirm(false);
  };

  const handleCancelFloorPrice = () => {
    setOpenFloorPriceConfirm(false);
  };

  // --- "BLUEPRINT" THEME CONFIGURATION ---
  const previewTheme = {
    primary: '#4338ca',
    accent: '#6366f1',
    containerBg: '#eef2ff',
    borderStyle: '2.5px dashed #6366f1',
    tableBg: '#ffffff',
    highlight: '#fbbf24',
  };

  const renderTableRow = (bullion, index) => {
    const isChanged = changedPreviewProductConfig.find(
      changed => changed.productType === bullion.key
    );

    const isHavingDiscount =
      Number(bullion.manualPreviewPrice) <
      Number(bullion.manualPreviewBoardPrice);
    return (
      <TableRow
        key={bullion.key}
        onClick={e => {
          if (e.target.type !== 'checkbox') {
            openDialog(
              `${String(bullion.name || '')} گرمی`,
              <PriceDialog
                is995={!!bullion.key.includes('GB_995')}
                isPreview
                manualBasePriceDefault={
                  !!String(manualBasePrice)?.includes(',')
                    ? Number(manualBasePrice?.replace(/,/g, ''))
                    : Number(manualBasePrice)
                }
                trigger={trigger}
                trig={trig}
                strategyDefault={'MANUAL'}
                priceDefault={Number(bullion.manualPreviewPrice)}
                cutNumberDefault={bullion.manualPreviewCutNumber}
                profitPercentageDefault={bullion.previewManualMarginPercentage}
                manualPriceLessThanThreshhold={manualPriceLessThanThreshhold}
                manualPriceMoreThanThreshhold={manualPriceMoreThanThreshhold}
                setBullionsConfig={setBullionsConfig}
                bullionWeight={bullion.weight}
                onClick={data =>
                  bullion.dialogProps.onClick({ isPreview: true, data })
                }
              />
            );
          }
        }}
        sx={{
          borderLeft: isChanged
            ? `4px solid ${previewTheme.highlight}`
            : '4px solid transparent',
          backgroundColor: isChanged
            ? 'rgba(251, 191, 36, 0.08)'
            : index % 2 === 0
              ? '#fff'
              : '#f8fafc',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            transform: 'scale(1.005)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            zIndex: 10,
            position: 'relative',
          },
          '& td': {
            borderBottom: '1px solid #e2e8f0',
            py: 1.8,
            px: 2,
            fontSize: '0.875rem',
            color: '#334155',
            fontWeight: isChanged ? 700 : 500,
          },
        }}
      >
        <TableCell>
          {isChanged && (
            <Box
              component="span"
              sx={{ color: '#d97706', mr: 1, fontSize: '0.7rem' }}
            >
              ●
            </Box>
          )}
          {String(bullion.name || '')}
        </TableCell>
        <TableCell>
          <Tooltip
            title={`هرگرم ${(Number(bullion.manualPreviewPrice) / bullion.weight).toLocaleString('fa-IR')} ریال`}
            arrow
            placement="top"
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: isHavingDiscount ? '#059669' : 'inherit' }}
            >
              {Number(bullion.manualPreviewPrice).toLocaleString('fa-IR')} ریال
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          {bullion.manualPreviewBoardPrice === -1
            ? 'بدون تخفیف'
            : `${Number(bullion.manualPreviewBoardPrice).toLocaleString('fa-IR')} ریال`}
        </TableCell>
        <TableCell>
          <span
            style={{
              color:
                Number(bullion.previewProfitMargin) > 0 ? '#16a34a' : '#dc2626',
              fontWeight: 700,
            }}
          >
            {Number(bullion.previewProfitMargin).toLocaleString('fa-IR')}%
          </span>
        </TableCell>
        <TableCell
          sx={{
            color: bullion?.competitorData?.tokeniko_active
              ? 'inherit'
              : '#94a3b8',
          }}
        >
          {!!bullion?.competitorData?.tokeniko
            ? `${Number(bullion?.competitorData?.tokeniko).toLocaleString('fa-IR')} ریال`
            : 'ناموجود'}
        </TableCell>
        <TableCell>
          <span
            style={{
              color:
                Number(bullion.previewManualMarginPercentage) > 0
                  ? '#16a34a'
                  : '#dc2626',
              fontWeight: 700,
            }}
          >
            {bullion.previewManualMarginPercentage !== null
              ? `${Number(bullion.previewManualMarginPercentage ?? 0).toLocaleString('fa-IR')}%`
              : '---'}
          </span>
        </TableCell>
        <TableCell>
          {bullion.manualPreviewCutNumber !== null
            ? `${Number(bullion.manualPreviewCutNumber ?? 0).toLocaleString('fa-IR')} رقم`
            : '---'}
        </TableCell>
      </TableRow>
    );
  };

  const renderDividerRow = () => (
    <TableRow>
      <TableCell
        colSpan={7}
        sx={{ p: 0, height: '16px', bgcolor: 'transparent', border: 'none' }}
      />
    </TableRow>
  );

  return (
    <Grid item xs={12} mt={3}>
      <Fade in={true} timeout={500}>
        <Box
          sx={{
            borderRadius: '24px',
            border: previewTheme.borderStyle,
            backgroundColor: previewTheme.containerBg,
            p: { xs: 2, sm: 3 },
            position: 'relative',
          }}
        >
          {/* دکمه بستن در گوشه چپ به صورت کوچک، بدون پس‌زمینه و فاقد کادر */}
          <Tooltip title="لغو تغییرات" arrow>
            <IconButton
              onClick={() => setShowPreview(false)}
              sx={{
                position: 'absolute',
                top: { xs: 12, sm: 20 },
                right: { xs: 12, sm: 20 },
                color: '#dc2626',
                bgcolor: 'transparent',
                border: 'none',
                p: 0.5,
                zIndex: 10,
                '&:hover': {
                  bgcolor: 'rgba(220, 38, 38, 0.08)',
                  color: '#b91c1c',
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          </Tooltip>

          {/* --- Action Header --- */}
          <Stack
            width={'100%'}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start' }}
            mb={4}
            mt={1}
            gap={3}
          >
            {/* عنوان و توصیف */}
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color={previewTheme.primary}
                letterSpacing="-0.5px"
              >
                بررسی نهایی قیمت‌ها
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
              >
                تغییرات شما هنوز ذخیره نشده‌اند. لطفا بررسی و تایید کنید.
              </Typography>
            </Box>

            {/* سوئیچ تنظیمات و دکمه اصلی در کنار هم */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              width={'100%'}
              justifyContent={'space-between'}
            >
              {/* دکمه دوحالته با استایل همسان دایلاگ */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: calculateFloorPriceForSell ? '#FEF2F2' : '#ffffffff',
                  border: calculateFloorPriceForSell
                    ? '1px solid #FCA5A5'
                    : '1px dashed #FCA5A5',
                  borderRadius: '12px',
                  px: 2,
                  py: 0.75,
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: calculateFloorPriceForSell ? '#991B1B' : '#334155',
                    fontSize: '0.8rem',
                    transition: 'color 0.2s ease-in-out',
                    userSelect: 'none',
                  }}
                >
                  نادیده گرفتن قیمت مرجع
                </Typography>
                <Switch
                  checked={calculateFloorPriceForSell}
                  onChange={handleToggleSwitchChange}
                  color="error"
                  size="small"
                />
              </Box>

              {/* دکمه ذخیره قیمت‌ها */}
              <Button
                variant="contained"
                onClick={() =>
                  isAnyChecked ? setOpenConfirmModal(true) : handleSave()
                }
                disabled={
                  isPreviewSubmitting ||
                  manualPriceLessThanThreshhold ||
                  manualPriceMoreThanThreshhold
                }
                startIcon={
                  isPreviewSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <PublishRoundedIcon />
                  )
                }
                sx={{
                  borderRadius: '12px',
                  bgcolor: previewTheme.primary,
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 8px 20px -4px rgba(67, 56, 202, 0.5)',
                  '&:hover': {
                    bgcolor: '#3730a3',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 24px -4px rgba(67, 56, 202, 0.6)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#cbd5e1',
                    color: '#94a3b8',
                  },
                }}
              >
                {isPreviewSubmitting
                  ? 'در حال ذخیره...'
                  : 'اعمال و ذخیره قیمت‌ها'}
              </Button>
            </Stack>
          </Stack>

          {/* --- Data Table --- */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    'شمش',
                    'قیمت (پیش‌نمایش)',
                    'قیمت تابلو',
                    'تفاوت با مرجع قیمت',
                    'قیمت رقیب',
                    'سود دستی',
                    'گردشده',
                  ].map(head => (
                    <TableCell
                      key={head}
                      sx={{
                        background: `repeating-linear-gradient(
                                    45deg,
                                    ${previewTheme.primary},
                                    ${previewTheme.primary} 10px,
                                    ${previewTheme.accent} 10px,
                                    ${previewTheme.accent} 20px
                                )`,
                        color: '#fff',
                        fontWeight: 800,
                        py: 2,
                        px: 2,
                        fontSize: '0.85rem',
                        whiteSpace: 'nowrap',
                        textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody sx={{ bgcolor: previewTheme.tableBg }}>
                {firstCategory.map((bullion, index) =>
                  renderTableRow(bullion, index)
                )}
                {renderDividerRow()}
                {secondCategory.map((bullion, index) =>
                  renderTableRow(bullion, index + 7)
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Fade>

      {/* --- Confirmation Modal (تغییرات حساس) --- */}
      <Dialog
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle
          sx={{
            color: '#dc2626',
            fontWeight: 800,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <WarningIcon />
          تایید تغییرات حساس
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
            <AlertTitle>توجه</AlertTitle>
            شما در حال اعمال تغییرات دستی روی موارد زیر هستید:
          </Alert>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {checkedBullions.map(b => (
              <Box
                key={b.key}
                sx={{
                  bgcolor: '#fff7ed',
                  color: '#c2410c',
                  border: '1px solid #fdba74',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                {b.name}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenConfirmModal(false)}
            sx={{ color: '#64748b' }}
          >
            بازگشت
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSave}
            disabled={isPreviewSubmitting}
            sx={{ borderRadius: '10px' }}
          >
            {isPreviewSubmitting ? '...' : 'تایید نهایی'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmFloorPriceDialog
        open={openFloorPriceConfirm}
        onClose={handleCancelFloorPrice}
        onConfirm={handleConfirmFloorPrice}
        title="تایید نادیده گرفتن قیمت مرجع"
        message="با این اقدام، قیمت‌گذاری بدون سنجش با مرجع اصلی (اتحادیه/فراز) انجام خواهد شد و ریسک مالی افزایش می‌یابد. آیا مطمئن هستید؟"
      />
    </Grid>
  );
};

export default PreviewPrice;
